<template>
  <div class='checkin'>
    <checkin-stepper class='checkin-stepper'/>

    <v-toolbar class='guest-navigator'>
      <v-toolbar-items>
        <v-btn
          icon
          @click='prevGuest({clearMsg: true})'
          :disabled='!hasPrevGuest || formIsDirty'
          v-visible='hasPrevGuest'
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
      </v-toolbar-items>

      <v-spacer/>

      <v-icon class='mr-3'>
        mdi-{{ guestTypologyIcon }}
      </v-icon>
      <v-toolbar-title v-if='fullname'>
        {{ fullname }}
      </v-toolbar-title>
      <v-toolbar-title v-else>
        {{ $t('guest.guest') }} {{ currentGuestIndex + 1 }}
      </v-toolbar-title>

      <v-spacer/>

      <v-toolbar-items>
        <v-btn
          icon
          @click='nextGuest({clearMsg: true})'
          :disabled='!hasNextGuest || formIsDirty || !roomChiefIsComplete'
          v-visible='hasNextGuest'
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <guests-stepper
      :currentStep='currentGuestIndex'
      :guests='guests'
      :guestCompletions='guestCompletions'
      v-if='missingGuestsAdded'
    />

    <div v-if='!loading'>
      <transition :name='transitionEffect'>
        <div
          class='guest-wrapper'
          v-for='(guest, index) in guests'
          :key='guest.id'
          v-if='currentGuestIndex == index'
        >
          <v-container class='checkin'>
            <v-card
              elevation='2'
              :class='["guest-card", { "room-chief": isGuestRoomChief(guest) }]'
            >
              <v-card-text>
                <v-alert
                  type='info'
                  colored-border
                  dense
                  color='primary lighten-2'
                  elevation='2'
                  border='left'
                  v-if='index == 0 && !isGuestSingle(guest)'
                >
                  {{ $t('guest.hints.room_chief') }}
                </v-alert>

                <guest-form
                  :ref='`edit-guest-form-${guest.id}`'
                  :order='order'
                  :guest='guest'
                  @form:submitted='handleGuestFormSubmittedOk'
                  @form:submittedWithError='handleGuestFormSubmittedWithError'
                  @form:completed='handleGuestFormCompleted'
                  @form:dirty='handleDirtyForm'
                  @form:invalid='handleInvalidForm'
                  @typology:room-chief-changed='handleRoomChiefChanged'
                ></guest-form>
              </v-card-text>
            </v-card>
          </v-container>
        </div>
      </transition>
    </div>

    <div v-else class='text-center'>
      <v-progress-circular
        size='50'
        color='primary'
        indeterminate
      />
    </div>

    <checkin-bottom-navigation>
      <template v-slot:prev-button>
        <v-btn v-if='formIsDirty' @click='handleUndoButtonClick()'>
          {{ $t('commons.cancel') }}
        </v-btn>
        <v-btn v-else @click='handleBackButtonClick()'>
          {{ $t('commons.prev') }}
        </v-btn>
      </template>
      <template v-slot:next-button>
        <v-btn
          v-if='formIsDirty'
          type='submit'
          color='success'
          :form='`edit-guest-form-${currentGuest.id}`'
          :loading='submitting'
        >
          {{ $t('commons.confirm') }}
          <v-icon v-if='formIsFailed' right dark>mdi-emoticon-sad-outline</v-icon>
        </v-btn>
        <v-btn v-else color='primary' @click='handleNextButtonClick()' :disabled='!roomChiefIsComplete'>
          {{ $t('commons.next') }}
        </v-btn>
      </template>
    </checkin-bottom-navigation>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import CheckinStepper from '../../checkin/checkin_stepper'
import CheckinBottomNavigation from '../../checkin/checkin_bottom_navigation'
import GuestForm from '../../guests/guest_form'
import GuestsStepper from '../../checkin/guests_stepper'
import { guestMixin } from '../../../mixins/guest_mixin'
import { orderFetchMixin } from '../../../mixins/order_fetch_mixin'

export default {
  name: 'CheckinGuests',
  components: {
    CheckinStepper,
    CheckinBottomNavigation,
    GuestForm,
    GuestsStepper,
  },
  mixins: [
    guestMixin,
    orderFetchMixin
  ],
  created() {
    this.fetchOrder({})
  },
  updated () {
    // Casi in cui entra
    // Caso 1: quando c'è il riconoscimenti E la pratica è in signing
    // OPPURE
    // Caso 2: quando non c'è il riconoscimento E la pratica è inviata
    if((this.reservation.guests_recognition==true &&  this.reservation.customer_contract_state == "signing") ||
      (this.reservation.guests_recognition==false &&  this.reservation.customer_contract_state == "sent")) {
      this.$router.push({name: 'checkinContract'})
    }
  },
  data() {
    return {
      currentGuestIndex: 0,
      previousGuestIndex: null,
      formIsDirty: false,
      formIsInvalid: false,
      formIsFailed: null,
      // Attualmente ricarichiamo sempre l'ordine dopo il submit
      // refetchOrderOnSubmit: false,
      roomChiefTypology: null,
      guestCompletions: {},
      missingGuests: [],
      missingGuestsAdded: false,
    }
  },

  computed: {
    guests() {
      return !!this.bookedGuestsCount ? this.reservation.guests.concat(this.missingGuests) : null
    },
    currentGuest() {
      return this.guests && this.guests[this.currentGuestIndex]
    },
    hasNextGuest() {
      return this.currentGuestIndex < this.bookedGuestsCount - 1
    },
    hasPrevGuest() {
      return this.currentGuestIndex > 0
    },
    roomChiefIsComplete() {
      return this.guestCompletions[this.roomChiefId]
    },
    fullname() {
      if (!this.currentGuest) return

      return [this.currentGuest.first_name, this.currentGuest.last_name]
        .filter(e => e)
        .join(' ')
    },
    currentFormId() {
      return `edit-guest-form-${this.currentGuest.id}`
    },
    transitionEffect() {
      return this.previousGuestIndex && this.previousGuestIndex > this.currentGuestIndex ? 'slide-fade-right' : 'slide-fade-left'
    },
    ...mapState('layout', ['loading', 'submitting', 'currentStep']),
    ...mapState('order', ['order']),
  },
  methods: {
    ...mapActions('layout', ['goToNextStepper', 'goToStepper']),
    fetchOrder({fresh=false, nextGuest=false}) {
      let order_id = this.$route.params.uuid || this.$route.params.order_id

      this.$store.dispatch('order/fetchOrder', { id: order_id, fresh: fresh })
        .then(order => {
          // console.log(`checkin_guest fetch order ${order.id} ${order.code} customer ${order.customer_id}`)

          // set current reservation to store, taken from url param
          this.$store.dispatch('order/setReservationId', this.reservation?.id)
            .then(() => this.$store.dispatch('layout/checkSteps'))

          this.$store.dispatch('mappings/fetchGuestRoomChiefTypologies')
            .then(guestRoomChiefTypologies => {
              this.reservation.guests.forEach(guest => this.setComplete(guest.id))
              this.missingGuests = []
              this.addMissingGuests({nextGuest: nextGuest})
              // Spostato nel form per rendere il caricamento dinamico in base alla scelta di citizenship
              // this.$store.dispatch('mappings/fetchIdentificationDocuments')
            })
            .catch(error => {
              console.log(`Cannot fetchIdentificationDocuments`, error)
            })
        })
        .catch(error => {
          this.$router.push({ name: 'dashboard' })
          this.$store.dispatch('layout/replaceAlert', {
            type: 'error',
            key: 'order_not_found'
          })
          console.log(`Cannot fetch order id ${order_id}`, error)
        })
    },
    async addMissingGuests({nextGuest=false}) {
      // console.log(`addMissingGuests`)
      if (!this.reservation) {
        console.log(` ops, reservation not present :-(`)
        return
      }
      const missingGuestsCount = this.bookedGuestsCount - this.insertedGuestsCount
      // console.log(` -> this.bookedGuestsCount (${this.bookedGuestsCount}) - this.reservation.guests.length (${this.reservation.guests.length}) = ${missingGuestsCount}`)

      // console.log(` -> roomChiefTypologies`, this.roomChiefTypologies)
      let roomChief =
        this.guests &&
        this.guests.find(guest => {
          return this.roomChiefTypologies.find(typology => {
            return typology.value == guest.typology
          })
        })
      // console.log(` -> searching roomChief`, roomChief)

      this.roomChiefTypology = roomChief && roomChief.typology
      let needRoomChief = !this.roomChiefTypology
      let typologyName
      for (let i = 0; i < missingGuestsCount; i++) {
        // console.log(` ...preparing missing guest index ${i}`)
        if (needRoomChief && !!this.roomChiefTypologies && this.roomChiefTypologies.length > 0) {
          this.roomChiefTypology = this.roomChiefTypologies[0].value
          typologyName = this.roomChiefTypology
          needRoomChief = false
        } else {
          // Cerchiamo una tipologia coerente a quella del capogruppo
          await this.$store.dispatch('mappings/fetchCoherentMemberTypology', this.roomChiefTypology )
            .then( typology => {
              // console.log(' fetchCoherentMemberTypology ok', typology)
              typologyName = typology
            })
            .catch( error => {
              // TODO: Cosa fare se non la troviamo?
              console.log(error)
            })
        }
        // console.log(` typologyName ${typologyName}`)
        this.missingGuests.push({
          id: 'new-guest-' + i,
          new: true,
          reservation_id: this.reservation.id,
          typology: typologyName,
          first_name: null,
          last_name: null,
          birthdate: null,
          sex: null,
          fiscal_code: null,
          email: null,
          document_number: null,
          phone_prefix: null,
          phone: null,
          identification_document: null,
          citizenship: null,
          country_of_birth: null,
          place_of_birth: null,
          country_of_residence: null,
          city_of_residence: null,
          address: null,
          zip: null,
          document_issued_location: null
        })
      }
      if (nextGuest) this.goNext({clearMsg: false})
      this.missingGuestsAdded = true
    },
    isGuestRoomChief(guest) {
      return !!guest && !!guest.typology && !!guest.typology.includes('HEAD')
    },
    isGuestSingle(guest) {
      return !!guest && !!guest.typology && !!guest.typology.includes('SINGLE')
    },
    nextGuest({clearMsg}) {
      // console.log(`nextGuest clearMsg:`, clearMsg)
      if (this.hasNextGuest && !this.formIsDirty) {
        this.previousGuestIndex = this.currentGuestIndex
        this.currentGuestIndex += 1
        window.scrollTo(0, 0)
        if (clearMsg) this.$store.dispatch('layout/clearAlerts')
        return true
      }
      return false
    },
    prevGuest({clearMsg}) {
      if (this.hasPrevGuest && !this.formIsDirty) {
        this.previousGuestIndex = this.currentGuestIndex
        this.currentGuestIndex -= 1
        window.scrollTo(0, 0)
        if (clearMsg) this.$store.dispatch('layout/clearAlerts')
        return true
      }
      return false
    },
    handleGuestFormSubmittedWithError({ guestId }) {
      this.formIsFailed = true
      window.scrollTo(0, 0)
      // console.log('handleGuestFormSubmittedWithError')
    },
    async handleGuestFormSubmittedOk({ guestId, newGuest }) {
      // console.log(`handleGuestFormSubmittedOk guestId: ${guestId}, newGuest: ${newGuest}`)
      this.formIsFailed = false
      this.formIsDirty = false

      // this.missingGuestsAdded = false
      this.setComplete(guestId)

      const key_message = newGuest ? 'guest_created' : 'guest_updated'
      this.$store.dispatch('layout/addAlert', {
        type: 'success',
        key: key_message
      })
      window.scrollTo(0, 0)

      if (newGuest) {
        // Ricarica l'ordine e tutta la sua struttura aggiornata. Questo è uno spreco di risorse ma attualmente è il modo
        // più semplice per assegnare l'id corretto al form dopo la creazione  (rimpiazzare l'id fittizio new-x con
        // l'id effettivo del guest)
        await this.fetchOrder({fresh: true, nextGuest: true})
      } else {
        const fresh = this.isRoomChiefOrSingle ? true : false
        this.fetchOrder({fresh: fresh, nextGuest: false})
      }
      // console.log('handleGuestFormSubmittedOk ospiti ', this.order.reservations[0].guests.length)
      // Non dovrebbe più servire in quanto i form vengono ricreati completamente
      // this.guests && this.guests.forEach(member => {
      //   this.$refs[`edit-guest-form-${member.id}`][0].refreshGuest()
      // })
      // this.refetchOrderOnSubmit = false

    },
    handleGuestFormCompleted({ guestId }) {
      this.setComplete(guestId)
    },
    setComplete(guestId) {
      this.$set(this.guestCompletions, guestId, true)
    },
    goNext({clearMsg}) {
      this.nextGuest({clearMsg}) || this.goToNextStepper()
        // this.$router.push({name: 'checkinContract'})
    },
    goBack({clearMsg}) {
      this.prevGuest({clearMsg}) ||
        this.$router.push({
          name: 'orderShow',
          params: { id: this.order.id },
        })
    },
    handleNextButtonClick() {
      this.goNext({clearMsg: true})
    },
    handleBackButtonClick() {
      this.goBack({clearMsg: true})
    },
    handleUndoButtonClick() {
      this.$refs[this.currentFormId][0].undoChanges()
      this.formIsFailed = false
    },
    async handleRoomChiefChanged({ guestId, typology }) {
      this.roomChiefTypology = typology
      // this.refetchOrderOnSubmit = true
    },
    handleDirtyForm(isDirty) {
      // console.log('checkin_guest handleDirtyForm')
      this.formIsDirty = isDirty
    },
    handleInvalidForm(isInvalid) {
      // console.log('checkin_guest handleInvalidForm')
      this.formIsInvalid = isInvalid
    },
  },
  // Commentato per non introdurre il bug della ricarica del form, in tal caso gli ospiti mancanti vengono duplicati
  // watch: {
  //   reservation: function(newVal) {
  //     this.addMissingGuests({})
  //   },
  // },
  directives: {
    visible: {
      update: function(el, binding, vnode) {
        el.style.opacity = binding.value === true ? 1 : 0
      },
    },
  },
}
</script>

<style lang='scss'>
  $topbar-height: 56px;
  $stepper-height: 98px;
  $stepper-sm-height: 77px;
  $guest-navigator-height: 36px;

  .v-stepper.checkin-stepper {
    position: sticky;
    z-index: 3;
    box-shadow: none;
    border-bottom: 1px solid #ddd;
    border-radius: 0;
    top: $topbar-height;
  }
  .v-toolbar.guest-navigator {
    position: sticky;
    z-index: 2;
    height: $guest-navigator-height !important;
    top: #{$topbar-height + $stepper-height};
    box-shadow: none !important;
    border-bottom: 1px solid #ddd;

    .v-toolbar__content {
      height: 100% !important;
      border-bottom: 1px dotted #eee;
    }
  }

  .checkin .v-stepper.v-stepper--alt-labels.guests-stepper {
    position: sticky;
    z-index: 1;
    top: #{$topbar-height + $stepper-height + $guest-navigator-height};
    .v-stepper__header {
      padding-top: 8px;
      padding-bottom: 8px;
    }
  }

  @media only screen and (max-width: 959px) {
    .v-toolbar.guest-navigator {
      top: #{$topbar-height + $stepper-sm-height};
    }
    .checkin .v-stepper.v-stepper--alt-labels.guests-stepper {
      top: #{$topbar-height + $stepper-sm-height + $guest-navigator-height};
    }
  }

  /* From left to right */
  .slide-fade-right-enter {
    transform: translateX(-100px);
    opacity: 0;
  }
  .slide-fade-right-enter-active,
  .slide-fade-right-leave-active {
    transition: all 0.3s ease;
  }
  .slide-fade-right-leave-to {
    transform: translateX(100px);
    opacity: 0;
  }

  /* From right to left */
  .slide-fade-left-enter {
    transform: translateX(100px);
    opacity: 0;
  }
  .slide-fade-left-enter-active,
  .slide-fade-left-leave-active {
    transition: all 0.3s ease;
  }
  .slide-fade-left-leave-to {
    transform: translateX(-100px);
    opacity: 0;
  }
</style>
